import { vec2 } from "gl-matrix";
import { TraversabilityType } from "@spi/Common/Geometry";
import { ObjectPool, PoolableObject } from '@spi/Common/Util';

export enum SpanType {
    Solid,
    Open,
    Conditional
}

export enum NeighbourDirection {
    East = "e",
    North = "n",
    West = "w",
    South = "s"
}

type CompassNeighbours = {
    [key in NeighbourDirection]?: HeightSpan
}

let neighbourOffsets = [
    { offset: [1, 0], direction: NeighbourDirection.East },
    { offset: [0, 1], direction: NeighbourDirection.North },
    { offset: [-1, 0], direction: NeighbourDirection.West },
    { offset: [0, -1], direction: NeighbourDirection.South }
];

export class HeightSpan implements PoolableObject {

    public nextSpan: HeightSpan;
    public spanType: SpanType;
    public traversable: boolean;
    public traversability: TraversabilityType;
    private _stepHeight: number;

    public groupReference: string;
    public isBorderSpan: boolean;
    public compassNeighbours: CompassNeighbours;

    constructor(
        public spanLocation: vec2,
        public bottom: number,
        public top: number) {

        this.clear();
    }

    public set stepHeightOverride(s: number) {
        if (this._stepHeight === null) {
            this._stepHeight = s;
        }
    }

    public get stepHeightOverride(): number {
        return this._stepHeight;
    }

    public determineNeighbours(map: Map<string, HeightSpan[]>, maxStepHeight: number) {

        let compassNeighbours: CompassNeighbours = {};
        let neighbourCount = 0;
        for (let offset of neighbourOffsets) {
            let x = this.spanLocation[0] + offset.offset[0];
            let y = this.spanLocation[1] + offset.offset[1];
            let id = x + ',' + y;

            if (map.has(id)) {
                for (let span of map.get(id)) {

                    if (!this.canStepTo(span, maxStepHeight)) {
                        continue;
                    }

                    if (compassNeighbours[offset.direction]) {
                        console.warn('Additional compass neighbour found in same direction, overwriting, but this may result in invalid navigation nodes. Symtoms include tiny 1x1 nodes.');
                    }

                    compassNeighbours[offset.direction] = span;
                    neighbourCount++;
                }
            }

        }

        this.compassNeighbours = compassNeighbours;
        this.isBorderSpan = neighbourCount !== neighbourOffsets.length;
    }

    public attachExtraNeighbours(map: Map<string, HeightSpan[]>, maxStepHeight: number) {

        let neighbourCount = 0;
        for (let offset of neighbourOffsets) {

            if (offset.direction in this.compassNeighbours) {
                neighbourCount++;
            } else {
                let x = this.spanLocation[0] + offset.offset[0];
                let y = this.spanLocation[1] + offset.offset[1];
                let id = x + ',' + y;

                if (map.has(id)) {
                    for (let span of map.get(id)) {

                        if (!this.canStepTo(span, maxStepHeight)) {
                            continue;
                        }

                        this.compassNeighbours[offset.direction] = span;
                        neighbourCount++;
                    }
                }
            }

        }

        this.isBorderSpan = neighbourCount !== neighbourOffsets.length;
    }

    private canStepTo(span: HeightSpan, maxStepHeight: number): boolean {
        let stepHeight = Math.max(
            this._stepHeight === null ? maxStepHeight : this._stepHeight,
            span._stepHeight === null ? maxStepHeight : span._stepHeight
        );

        return Math.abs(span.bottom - this.bottom) <= stepHeight;
    }

    public clear(): void {
        this.nextSpan = null;
        this.traversable = false;
        this.isBorderSpan = false;
        this.spanType = SpanType.Solid;
        this._stepHeight = null;
        this.compassNeighbours = {};
    }
}
